/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, reaction, sleep } from '@reatom/framework'
import { withLocalStorage } from '@reatom/persist-web-storage'

import { isLoggedInAtom } from '@root/entities/auth/atoms'
import { terminalAccessGrantedAtom } from '@root/features/access/atoms/reatom-terminal-access'
import { chooseDoor } from '@root/features/access/model/choose-door'
import { onInitializationReadyOrWebsiteAction } from '@root/features/initialization/atoms/common'
import {
  isTauriOrBrowserLocalAtom,
  scaleFactorAtom,
  STORAGE_PREFIX,
} from '@root/shared'

const reatomOnboardingDialogs = () => {
  const acknowledgeDialogSeen = atom(false, 'acknowledgeDialogSeen').pipe(
    withLocalStorage(`${STORAGE_PREFIX}/v1/skipOnboarding`),
  )
  const scaleFactorDialogSeenAtom = atom(
    false,
    'acknowledgeDialogOpenAtom',
  ).pipe(withLocalStorage(`${STORAGE_PREFIX}/v1/scaleFactorDialogSeen`))

  const acknowledgeDialogOpenAtom = atom(false, 'acknowledgeDialogOpenAtom')
  const scaleFactorDialogOpenAtom = atom(false, 'scaleFactorDialogOpenAtom')

  /**
   * The acknowledgement waits until somebody is actually at the terminal.
   *
   * It used to open as soon as the app initialised, which is before the front
   * door has decided anything — so a new visitor got "you confirm that you
   * are 18+ and accept our Terms" stacked on top of the invite-only screen
   * they had not read yet. Two gates at once, and the one in front was about
   * terms for a product they had not been let into.
   *
   * `chooseDoor` is the single rule for where somebody is, so this asks it
   * rather than keeping a second opinion.
   */
  const atTheTerminalAtom = atom(
    (ctx) =>
      chooseDoor({
        isLoggedIn: ctx.spy(isLoggedInAtom),
        isApp: ctx.spy(isTauriOrBrowserLocalAtom),
        granted: ctx.spy(terminalAccessGrantedAtom),
      }) === 'terminal',
    'atTheTerminalAtom',
  )

  const initialize = action((ctx) => {
    onInitializationReadyOrWebsiteAction(ctx, () => {
      onTerminalReaction(ctx)
    })
  }, 'initialize')

  const onTerminalReaction = reaction((ctx) => {
    if (!ctx.spy(atTheTerminalAtom)) {
      return
    }
    const skipOnboarding = ctx.get(acknowledgeDialogSeen)
    if (!skipOnboarding) {
      acknowledgeDialogOpenAtom(ctx, true)
      return
    }
    showScaleFactorDialogAction(ctx)
  }, 'onTerminalReaction')

  const showScaleFactorDialogAction = action((ctx) => {
    const scaleFactorAcknowledged = ctx.get(scaleFactorDialogSeenAtom)
    if (scaleFactorAcknowledged) {
      return
    }
    const scaleFactor = ctx.get(scaleFactorAtom)
    if (scaleFactor?.source === 'automatic') {
      scaleFactorDialogOpenAtom(ctx, true)
    }
  }, 'showScaleFactorDialogAction')

  const closeAcknowledgeDialogAction = action(async (ctx) => {
    acknowledgeDialogOpenAtom(ctx, false)
    acknowledgeDialogSeen(ctx, true)
    await ctx.schedule(() => sleep(150))
    showScaleFactorDialogAction(ctx)
  }, 'closeAcknowledgeDialogAction')

  const closeScaleFactorDialogAction = action((ctx) => {
    scaleFactorDialogOpenAtom(ctx, false)
    scaleFactorDialogSeenAtom(ctx, true)
  }, 'closeScaleFactorDialogAction')

  return {
    initialize,
    acknowledgeDialogOpenAtom,
    scaleFactorDialogOpenAtom,
    closeAcknowledgeDialogAction,
    closeScaleFactorDialogAction,
  }
}
export const onboardingDialogsModel = reatomOnboardingDialogs()
